import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../../components/FeatherIcon';
import { crmFetch, dataOf, listOf } from '../../chat/api';
import type { CrmAccount } from '../../crm/crmAccess';
import { ACTIVITY_KINDS, HANDOFF_GROUP_IDS, type ActivitySettings } from '../activity';
import type { AccountColour } from '../colour';
import { AccountDropdown, ListDropdown } from './Dropdown';

interface Props {
  value: ActivitySettings;
  accounts: CrmAccount[];
  colourOf: (accountId: string) => AccountColour;
  onChange: (next: ActivitySettings) => void;
  onClose: () => void;
}

function Toggle({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" className={`ws-toggle-row ${on ? 'is-on' : ''}`} onClick={onClick} aria-pressed={on}>
      <span className="ws-check">{on && <FeatherIcon name="check" size={10} />}</span>
      {label}
    </button>
  );
}

export function ActivityFilters({ value, accounts, colourOf, onChange, onClose }: Props) {
  const { t } = useTranslation('workspace');
  const [listAccount, setListAccount] = useState(() => accounts.find((a) => value.watchedLists[a.account_id]?.length)?.account_id ?? accounts[0]?.account_id ?? '');
  const [lists, setLists] = useState<{ id: string; name: string }[] | null>(null);
  const set = (patch: Partial<ActivitySettings>) => onChange({ ...value, ...patch });

  useEffect(() => {
    if (!listAccount) return;
    let alive = true;
    setLists(null);
    crmFetch('/v1/lists/get', { accountId: listAccount, body: { limit: 200, offset: 0 } })
      .then((b) => alive && setLists(listOf(dataOf(b)).map((l) => ({ id: String(l.id), name: String(l.name || l.listName || l.id) }))))
      .catch(() => alive && setLists([]));
    return () => {
      alive = false;
    };
  }, [listAccount]);

  const watched = value.watchedLists[listAccount] || [];
  const toggleList = (name: string) => {
    const next = watched.includes(name) ? watched.filter((n) => n !== name) : [...watched, name];
    set({ watchedLists: { ...value.watchedLists, [listAccount]: next } });
  };

  return (
    <div className="ws-menu ws-filters" onMouseDown={(e) => e.stopPropagation()}>
      <div className="ws-filters-head">
        <b>{t('activity.filters.title')}</b>
        <button type="button" className="ws-icon-btn" onClick={onClose} title={t('common.close')}>
          <FeatherIcon name="x" size={14} />
        </button>
      </div>
      <div className="ws-menu-scroll">
        <span className="ws-menu-section">{t('activity.filters.kinds')}</span>
        {/* Watched list has its own switch next to the list choice below. */}
        {ACTIVITY_KINDS.filter((k) => k !== 'list').map((k) => (
          <Toggle key={k} on={value.kinds[k]} label={t(`activity.kinds.${k}`)} onClick={() => set({ kinds: { ...value.kinds, [k]: !value.kinds[k] } })} />
        ))}

        <span className="ws-menu-section">{t('activity.filters.messagesFrom')}</span>
        <div className="ws-seg">
          {[0, 1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" className={value.minBuyingPower === n ? 'is-on' : ''} onClick={() => set({ minBuyingPower: n })}>
              {n === 0 ? t('activity.filters.anyBp') : `${n}+`}
            </button>
          ))}
        </div>
        <Toggle on={value.includeUnknownBp} label={t('activity.filters.unknownBp')} onClick={() => set({ includeUnknownBp: !value.includeUnknownBp })} />

        <span className="ws-menu-section">{t('activity.filters.minAmount')}</span>
        <label className="ws-amount">
          $
          <input
            inputMode="decimal"
            value={value.minAmount || ''}
            placeholder="0"
            onChange={(e) => set({ minAmount: Math.max(0, Number(e.target.value.replace(/[^\d.]/g, '')) || 0) })}
          />
        </label>

        <span className="ws-menu-section">{t('activity.filters.hot')}</span>
        <div className="ws-seg">
          {(['change', 'every'] as const).map((m) => (
            <button key={m} type="button" className={value.hotMode === m ? 'is-on' : ''} onClick={() => set({ hotMode: m })}>
              {t(`activity.filters.hotMode.${m}`)}
            </button>
          ))}
        </div>

        <span className="ws-menu-section">{t('activity.filters.handoffs')}</span>
        {HANDOFF_GROUP_IDS.map((g) => (
          <Toggle key={g} on={value.handoffGroups[g]} label={t(`activity.groups.${g}`)} onClick={() => set({ handoffGroups: { ...value.handoffGroups, [g]: !value.handoffGroups[g] } })} />
        ))}

        <span className="ws-menu-section">{t('activity.filters.lists')}</span>
        <Toggle on={value.kinds.list} label={t('activity.filters.listsOn')} onClick={() => set({ kinds: { ...value.kinds, list: !value.kinds.list } })} />
        {value.kinds.list && accounts.length > 0 && (
          <>
            <div className="ws-dd-stack">
              <AccountDropdown accounts={accounts} value={listAccount} onChange={setListAccount} colourOf={colourOf} badge={(id) => value.watchedLists[id]?.length ?? 0} />
              <ListDropdown key={listAccount} lists={lists} isOn={(l) => watched.includes(l.name)} onToggle={(l) => toggleList(l.name)} />
            </div>
            {watched.length > 0 && (
              <div className="ws-chips">
                {watched.map((name) => (
                  <button key={name} type="button" className="ws-chip is-on" title={t('dropdown.remove')} onClick={() => toggleList(name)}>
                    {name}
                    <FeatherIcon name="x" size={11} />
                  </button>
                ))}
              </div>
            )}
            {/* All Accounts; mostly spam, so off by default. */}
            <Toggle on={value.showAiIgnored} label={t('activity.filters.aiIgnored')} onClick={() => set({ showAiIgnored: !value.showAiIgnored })} />
          </>
        )}
      </div>
    </div>
  );
}
